<template>
  <div>
    <export-modal
      v-model="csvModal.show"
      file-type="csv"
      :file-link="csvModal.link"
      :alert-text="csvModal.alertText"
    />

    <h1>Vessel Details</h1>

    <b-row>
      <b-col cols="12">
        <search-panel @input="handleSearchInput" />
      </b-col>
    </b-row>

    <b-row>
      <b-col md="5" sm="12">
        <b-row>
          <b-col cols="12">
            <vessel-stat :stats="vessel" :live-stats="vesselDetails" />
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <port-calls-stat :port-calls="portCalls.items" />
          </b-col>
        </b-row>
      </b-col>
      <b-col md="7" sm="12" class="d-flex">
        <b-card no-body class="w-100">
          <div slot="header">
            Vessel Location and Historical Tracks
          </div>
          <b-card-body class="p-0">
            <vessel-map
              :vessel-details="vesselDetails"
              :start-date="startDate"
              :end-date="endDate"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <voyages
          :items="voyages.items"
          :sorting="allVoyagesLoaded"
          :currentPage="voyages.currentPage"
          :export-types="exportTypes"
          @page-changed="() => tablePageChanged('voyages', ...arguments)"
          @export="format => handleExport('voyages', format)"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <port-calls-table
          :items="portCalls.items"
          :sorting="allPortCallsLoaded"
          :currentPage="portCalls.currentPage"
          :export-types="exportTypes"
          @page-changed="() => tablePageChanged('portCalls', ...arguments)"
          @export="format => handleExport('portCalls', format)"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <drydock-stays
          title="Drydock stays"
          :items="drydockStays.items"
          :sorting="allPortCallsLoaded"
          :currentPage="drydockStays.currentPage"
          :export-types="exportTypes"
          @page-changed="() => tablePageChanged('drydockStays', ...arguments)"
          @export="format => handleExport('drydockStays', format)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import getNested from "lodash-es/get";
import pickBy from "lodash-es/pickBy";
import isEmpty from "lodash-es/isEmpty";

import { mapActions, mapGetters } from "vuex";
import Voyages from "../components/tradeflows/Voyages";
import SearchPanel from "../components/vesseldetails/SearchPanel";
import VesselStat from "../components/vesseldetails/VesselStat";
import VesselMap from "../components/vesseldetails/VesselMap";
import ExportModal from "../components/widgets/ExportModal";
import PortCallsStat from "@/components/vesseldetails/PortCallsStat";
import PortCallsTable from "../components/vesseldetails/PortCallsTable";
import DrydockStays from "../components/vesseldetails/DrydockStays";
import debounce from "lodash-es/debounce";

export default {
  name: "vessel-details",

  components: {
    DrydockStays,
    ExportModal,
    VesselMap,
    Voyages,
    SearchPanel,
    PortCallsTable,
    VesselStat,
    PortCallsStat
  },

  data() {
    return {
      vessel: {},
      vesselDetails: null,
      startDate: null,
      endDate: null,

      tableSections: ["voyages", "portCalls", "drydockStays"],

      voyages: {
        items: [],
        apiItemsPerPage: 5000, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or max_page, it doesn't matter since pages are starts from '1'
        apiCurrentPage: null,
        currentPage: null,
        requiredFields: ["imo", "fromDate", "toDate"],
        method: this.getVoyages
      },

      portCalls: {
        items: [],
        apiItemsPerPage: 5000, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or max_page, it doesn't matter since pages are starts from '1'
        apiCurrentPage: null,
        currentPage: null,
        requiredFields: ["imo", "fromDate", "toDate"],
        method: this.getPortCalls
      },

      drydockStays: {
        items: [],
        apiItemsPerPage: 5000, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or max_page, it doesn't matter since pages are starts from '1'
        apiCurrentPage: null,
        currentPage: null,
        requiredFields: ["imo", "fromDate", "toDate"],
        method: this.getDrydockStays
      },

      apiFilterValues: {},

      exportTypes: ["csv"],
      csvModal: {
        show: false,
        link: null,
        alertText: null
      }
    };
  },

  methods: {
    ...mapActions("api/tradeflows", ["getVoyages", "getPortCalls"]),
    ...mapActions("api/drydock", ["getDrydockStays"]),
    ...mapActions("api/vessels", ["getVesselLiveDetails"]),

    async handleSearchInput(vessel, parameters) {
      const vesselIMO = getNested(vessel, "imo", null);
      await this.refreshURL(vesselIMO);

      if (vesselIMO) {
        this.vessel = vessel;
        this.startDate = parameters.startDate;
        this.endDate = parameters.endDate;

        this.applyFilterHandler({
          imo: [vesselIMO],
          fromDate: this.startDate,
          toDate: this.endDate
        });
      } else {
        this.resetDashboardData();
      }

      if (!isEmpty(vessel)) {
        window.analytics.track("VesselDetailsSearch", { imo: vesselIMO });
      }
    },

    async refreshURL(vesselIMO) {
      if (!vesselIMO) {
        await this.$router.push({ name: "Vessel Details" });
        return;
      }

      if (this.routeVesselIMO !== `${vesselIMO}`) {
        await this.$router.push({
          name: "Vessel Details",
          query: { imo: `${vesselIMO}` }
        });
      }
    },

    applyFilterHandler: debounce(function(apiFilterValues) {
      this.applyFilter(apiFilterValues);
    }, 400),

    async applyFilter(apiFilterValues) {
      this.apiFilterValues = apiFilterValues;

      if (this.apiFilterValues.imo) {
        await this.loadData(apiFilterValues);
      } else {
        this.resetDashboardData();
      }
    },

    async loadData(filterParameters) {
      this.vesselDetails = await this.getVesselLiveDetails([this.vessel.imo]);

      for (let sectionName of this.tableSections) {
        this[sectionName].items = [];
        this[sectionName].currentPage = 1;

        let parameters = {
          ...this.filterParametersByNames(
            filterParameters,
            this[sectionName].requiredFields
          ),
          page: 1,
          limit: this[sectionName].apiItemsPerPage
        };
        await this.fetchTableItems(sectionName, parameters);
      }
    },

    getBaseFilterParameters() {
      let parameters = {};
      Object.keys(this.apiFilterValues).forEach(fieldName => {
        if (!fieldName.startsWith("_")) {
          parameters[fieldName] = this.apiFilterValues[fieldName];
        }
      });

      delete parameters.berthId;
      delete parameters.direction;
      return parameters;
    },

    async handleExport(sectionName, format) {
      window.analytics.track('ExportVesselDetails-'+sectionName,this.filterParameters);
      const limit = 20000;
      const method = this[sectionName].method;
      let filters = { ...this.apiFilterValues };
      delete filters.fromDate;
      delete filters.toDate;
      let response = await method({
        ...filters,
        format,
        limit: limit
      });
      this.csvModal.link = response.data.csv;
      this.csvModal.alertText = `CSV download for voyages is currently limited to ${limit} rows per download`;
      this.csvModal.show = true;
    },

    async tablePageChanged(sectionName, currentPage, totalPages) {
      const section = this[sectionName];

      // table on demand loading, overview:
      // 1. detect when a current page is less than 'preloadPagesRange' value
      // 2. add items which are received by 'preloadRequestsCount' requests
      if (this.getActiveRequestsCount) {
        return; // prevent multiple requests
      }

      // calculate an API page number to start
      let apiPagesLoaded = section.items.length / section.apiItemsPerPage;
      const preloadPagesRange = 3;

      if (
        totalPages - currentPage < preloadPagesRange &&
        Number.isInteger(apiPagesLoaded)
      ) {
        let parameters = this.getBaseFilterParameters();
        parameters = this.filterParametersByNames(
          { ...parameters, page: apiPagesLoaded + 1 },
          section.requiredFields
        );

        await this.fetchTableItems(sectionName, parameters);

        // b-pagination resets a current page when data is changed we have to restore it this way
        // https://github.com/bootstrap-vue/bootstrap-vue/issues/3372
        section.currentPage = currentPage;
      }
    },

    filterParametersByNames: function(filterParameters, requiredFields) {
      // removes redundant parameters
      // (keeps only requiredFields from filterParameters)
      return pickBy(filterParameters, (value, name) => {
        return requiredFields.includes(name);
      });
    },

    async fetchTableItems(sectionName, filterParameters, requestsCount = 3) {
      let countdown = requestsCount; // no more than countdown requests
      let parameters = { ...filterParameters };
      let response = null;
      const section = this[sectionName];
      delete parameters.fromDate;
      delete parameters.toDate;

      do {
        response = (await section.method(parameters)) || {};

        if (response.data && response.data.data) {
          section.items.push.apply(section.items, response.data.data);
          section.apiTotalPages = response.data.maxPage;
          section.apiCurrentPage = response.data.currentPage;
          parameters.page += 1;
        } else {
          break;
        }
        countdown--;
      } while (parameters.page <= response.data.maxPage && countdown);
    },

    resetDashboardData() {
      for (let sectionName of this.tableSections) {
        this[sectionName].items = [];
        this[sectionName].apiTotalPages = null;
        this[sectionName].currentPage = null;
      }
      this.vessel = {};
      this.vesselDetails = null;
    }
  },

  computed: {
    ...mapGetters("api", ["getActiveRequestsCount"]),

    routeVesselIMO() {
      const vesselIMO = getNested(this, "$route.query.imo", null);
      return vesselIMO ? `${vesselIMO}` : vesselIMO;
    },

    allVoyagesLoaded() {
      return (
        this.voyages.apiTotalPages &&
        this.voyages.apiCurrentPage &&
        this.voyages.apiTotalPages === this.voyages.apiCurrentPage
      );
    },

    allPortCallsLoaded() {
      return (
        this.portCalls.apiTotalPages &&
        this.portCalls.apiCurrentPage &&
        this.portCalls.apiTotalPages === this.voyages.apiCurrentPage
      );
    },

    allDrydockStaysLoaded() {
      return (
        this.drydockStays.apiTotalPages &&
        this.drydockStays.apiCurrentPage &&
        this.drydockStays.apiTotalPages === this.voyages.apiCurrentPage
      );
    }
  }
};
</script>
